import { useCallback, useEffect, useState } from 'react';
import type { LoginDeviceRecord } from '../core/models/deviceTrust';
import { listStaffLoginDevices, setStaffLoginDeviceTrust } from '../services/deviceTrust';

export function useStaffDevicesPanel(
  targetUid: string | undefined,
  houseId: string | undefined,
) {
  const [devices, setDevices] = useState<LoginDeviceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!targetUid) return;
    setLoading(true);
    setError('');
    try {
      const rows = await listStaffLoginDevices(targetUid, houseId);
      setDevices(rows as LoginDeviceRecord[]);
    } catch (err) {
      setError((err as Error).message);
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }, [targetUid, houseId]);

  useEffect(() => {
    void load();
  }, [load]);

  const updateDevice = useCallback(async (
    deviceId: string,
    patch: { trusted?: boolean; blocked?: boolean },
  ) => {
    if (!targetUid) return;
    setLoading(true);
    setError('');
    try {
      const device = await setStaffLoginDeviceTrust({
        targetUid,
        deviceId,
        ...patch,
      });
      setDevices((current) => current.map((row) => (
        row.deviceId === deviceId ? { ...row, ...(device as LoginDeviceRecord) } : row
      )));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [targetUid]);

  return { devices, loading, error, reload: load, updateDevice };
}
